import { useEffect, useMemo, useState } from "react";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";
import { HiCurrencyDollar } from "react-icons/hi";
import { MdRemoveShoppingCart } from "react-icons/md";
import NoPictureWarning from "../resources/nopic.png";
import MenuService from "../rest/menuService";
import ClientService from "../rest/clientService";
import useFilters from "./reusables/filterBar/hooks/useFilters";
import buildQueryParams from "./reusables/filterBar/hooks/buildQueryParams";
import FilterBar from "./reusables/filterBar/filterBar";
import Pagination from "./lib/pagination";
import { formatCurrency } from "../utils/CurrencyUtils";
import SmartImage from "./reusables/smartImage";

const adjustPageSize = () => {
    if (window.innerWidth >= 1536) return 18;
    if (window.innerWidth >= 1280) return 15;
    if (window.innerWidth >= 1024) return 12;
    if (window.innerWidth >= 768) return 9;
    return 6;
};

const normalizeFilterValue = (item) => {
    if (item === null || item === undefined) return null;
    if (typeof item === "object" && "value" in item) return item.value;
    return item;
};

const ProductGridSale = ({
    selectedItems = [],
    onChooseItem,
    isCustomer
}) => {
    const [pageSize, setPageSize] = useState(adjustPageSize());
    const [pagedData, setPagedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showProductImages, setShowProductImages] = useState(true);
    const [categoryOptions, setCategoryOptions] = useState([]);

    const {
        search,
        setSearch,
        filters,
        setFilters,
        updateFilter,
        clearFilters,
        page,
        setPage,
    } = useFilters();

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const preferences = await ClientService.getClientAdminPreferences();
                setShowProductImages(preferences?.enable_product_images === true);
                if (preferences?.categories) {
                    const cats = preferences.categories
                        .split(";")
                        .map(c => c.trim())
                        .filter(c => c !== "")
                        .map(c => ({ value: c, label: c }));
                    setCategoryOptions(cats);
                }
            } catch (error) {
                console.error("Erro config:", error);
            }
        };
        loadPreferences();
    }, []);

    const productFilters = useMemo(() => {
        const configs = [];
        if (categoryOptions.length > 0) {
            configs.push({
                key: "categories",
                label: "Categorias",
                options: categoryOptions,
                selectedValues: filters.categories || [],
                onChange: values => updateFilter("categories", values),
                placeholder: "Categoria",
            });
        }
        return configs;
    }, [filters, updateFilter, categoryOptions]);

    const productRangeFilters = useMemo(() => {
        return [
            {
                key: "price",
                label: "Preço",
                min: filters.minPrice,
                max: filters.maxPrice,
                prefix: "R$",
                icon: HiCurrencyDollar,
                onChange: ({ min, max }) => {
                    setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }));
                    setPage(0);
                }
            }
        ];
    }, [filters, setFilters, setPage]);

    const mapFiltersToApi = (currentFilters) => {
        const apiFilters = {};
        if (currentFilters.categories?.length > 0) {
            apiFilters.categories = currentFilters.categories.map(normalizeFilterValue);
        }
        if (currentFilters.minPrice) apiFilters.minPrice = currentFilters.minPrice;
        if (currentFilters.maxPrice) apiFilters.maxPrice = currentFilters.maxPrice;
        return apiFilters;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const apiFilters = mapFiltersToApi(filters);
            const queryParams = buildQueryParams({
                search,
                filters: apiFilters,
                page,
                pageSize
            });

            const serviceParams = {
                search: queryParams.search,
                categories: queryParams.categories ? (Array.isArray(queryParams.categories) ? queryParams.categories : [queryParams.categories]) : [],
                minPrice: queryParams.minPrice,
                maxPrice: queryParams.maxPrice,
                currentPage: page,
                pageSize: pageSize
            };

            const response = await MenuService.getCompleteActiveMenu(serviceParams);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setPagedData(response.content);

        } catch (error) {
            console.error("Erro fetch produtos:", error);
            setPagedData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const onResize = () => setPageSize(adjustPageSize());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const timeout = setTimeout(fetchData, 300);
        return () => clearTimeout(timeout);
    }, [search, filters, page, pageSize, showProductImages]);

    const getSelectedQuantity = (id) => {
        const found = selectedItems.find((i) => i.id === id);
        return found ? found.quantity : 0;
    };

    return (
        <div className="flex-col justify-center w-full h-full">
            <div className="flex justify-center my-2 px-2">
                <FilterBar
                    searchValue={search}
                    onSearchChange={(val) => { setSearch(val); setPage(0); }}
                    onResetPage={() => setPage(0)}
                    filters={productFilters}
                    rangeFilters={productRangeFilters}
                    onClearFilters={clearFilters}
                    loading={loading}
                    searchPlaceholder="Buscar produto..."
                />
            </div>

            <div className="flex justify-center my-4 w-full">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4 w-full px-2 md:px-0">

                    {loading && (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 opacity-50">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom_blue mb-4"></div>
                            <p className="text-gray-500 font-medium">Carregando cardápio...</p>
                        </div>
                    )}

                    {!loading && pagedData.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400">
                            <p>Nenhum produto encontrado.</p>
                        </div>
                    )}

                    {!loading && pagedData.map((item) => {
                        const currentQuantity = getSelectedQuantity(item.id);
                        const isSoldOut = item.quantity === 0;
                        const hasSelection = currentQuantity > 0;
                        const imageBaseUrl = `/foodz/api/v1/uploads/carregarImagemDePerfil?path=${encodeURIComponent(item.image_url)}`;

                        return (
                            <div
                                key={item.id}
                                className={`
                                    group relative aspect-[4/5] rounded-2xl overflow-hidden transition-all duration-300 bg-white
                                    ${hasSelection ? "ring-4 ring-custom_blue shadow-xl" : "ring-1 ring-slate-200 hover:shadow-lg"}
                                    ${isSoldOut ? "grayscale opacity-75 cursor-not-allowed" : ""}
                                `}
                            >
                                <div className="absolute inset-0 w-full h-full">
                                    {showProductImages && item.image_url ? (
                                        <SmartImage
                                            src={imageBaseUrl}
                                            isThumb={true}
                                            alt={item.name}
                                            className="object-cover"
                                        />
                                    ) : (
                                        <img
                                            src={NoPictureWarning}
                                            className="opacity-40 object-contain p-4 w-full h-full"
                                            alt="Sem imagem"
                                        />
                                    )}
                                </div>

                                {isSoldOut && (
                                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[2px]">
                                        <span className="text-white font-bold text-md px-3 py-2 rounded"> {item.name}</span>
                                        <MdRemoveShoppingCart className="text-white text-4xl mb-2" />
                                        <span className="text-white font-bold text-lg uppercase tracking-wider border-2 border-white px-3 py-1 rounded">Esgotado</span>
                                    </div>
                                )}

                                {!isSoldOut && (
                                    <div className="absolute top-2 right-2 z-20 flex flex-col items-end gap-1">
                                        <span className={`text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm ${item.quantity < 10 ? "bg-orange-500" : "bg-black/50"}`}>
                                            Disp: {item.quantity}
                                        </span>
                                        {item.reserved_quantity > 0 && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm bg-amber-500 text-white">
                                                Res: {item.reserved_quantity}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />
                                <div className="absolute bottom-0 left-0 w-full p-3 flex flex-col z-10">
                                    <div className="mb-2">
                                        <div className="flex justify-between items-start">
                                            <h3 className="font-bold text-white text-sm md:text-base leading-tight line-clamp-2 drop-shadow-md flex-1 mr-2 break-words">
                                                {item.name}
                                            </h3>
                                        </div>
                                        <p className="text-lime-400 font-bold text-sm md:text-base mt-1">
                                            {formatCurrency(isCustomer ? (item.online_price || item.price) : item.price)}
                                        </p>
                                    </div>

                                    <div className={`
                                        flex items-center justify-between 
                                        bg-white/20 backdrop-blur-md border border-white/30 
                                        rounded-lg p-1 mt-auto
                                        ${isSoldOut ? "invisible" : ""}
                                    `}>
                                        <button
                                            type="button"
                                            onClick={() => onChooseItem(item, "minus")}
                                            disabled={isSoldOut || currentQuantity === 0}
                                            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/10 hover:bg-white/30 text-white rounded-md transition-colors disabled:opacity-30 active:scale-95"
                                        >
                                            <AiOutlineMinus className="md:text-lg" />
                                        </button>

                                        <span className="text-white font-bold text-base md:text-lg w-8 text-center drop-shadow-sm">
                                            {currentQuantity}
                                        </span>

                                        <button
                                            type="button"
                                            onClick={() => onChooseItem(item, "plus")}
                                            disabled={isSoldOut}
                                            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-custom_blue hover:bg-blue-600 text-white rounded-md shadow-md transition-colors active:scale-95"
                                        >
                                            <AiOutlinePlus className="md:text-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-center my-4 pb-10">
                {totalPages > 1 && (
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={totalElements}
                        pageSize={pageSize}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductGridSale;